import type { FC } from 'react';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { CircularProgress, Grid, Stack } from '@mui/material';
import { AccountStatusCard } from './AccountStatusCard';
import type { IProfileFormValues } from './ServiceProviderProfileForm';
import { ServiceProviderProfileInfo } from './ServiceProviderProfileForm';
import {
  useServiceProvider,
  useUpdateServiceProvider,
  useUserProfileApi,
} from '@pflegenavi/service-provider-frontend/api';
import { getGlobalServiceProviderIdFromToken } from '../../utils/getGlobalServiceProviderIdFromToken';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import type { UserProfileDto } from '@pflegenavi/service-provider-shared/api-endpoints';
import { ServiceProviderStripe } from './ServiceProviderStripe';
import { ServiceProviderStripeOperatedBy } from '@pflegenavi/shared/api';

export const ServiceProviderProfileDetails: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  // @ts-expect-error // getTokenImmediate is intentionally hidden. It is necessary here to exctract the globalServiceProviderId
  const { getTokenImmediate } = useAuthentication();

  const globalServiceProviderId = getGlobalServiceProviderIdFromToken(
    getTokenImmediate()
  );

  const { data: company } = useServiceProvider(
    {
      globalServiceProviderId: globalServiceProviderId ?? '',
    },
    {
      enabled: globalServiceProviderId !== undefined,
    }
  );

  const { mutateAsync: updateServiceProvider } = useUpdateServiceProvider();

  const userProfileApi = useUserProfileApi();
  const [userProfile, setUserProfile] = useState<UserProfileDto | undefined>(
    undefined
  );

  useEffect(() => {
    (async () => {
      const userProfile = await userProfileApi.getUserProfile();
      setUserProfile(userProfile);
    })();
  }, [userProfileApi]);

  const handleUpdateServiceProvider = useCallback(
    async (values: IProfileFormValues) => {
      try {
        await updateServiceProvider({
          data: {
            name: values.companyName,
            address: values.address,
            zipCode: values.zipCode,
            city: values.city,
            phone: values.phone,
            countryId: values.countryId,
          },
          globalServiceProviderId: globalServiceProviderId ?? '',
        });
        enqueueSnackbar(
          t('service-provider-app.profile.successfully-updated'),
          {
            variant: 'success',
          }
        );
      } catch {
        enqueueSnackbar(t('service-provider-app.profile.failed-to-update'), {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, globalServiceProviderId, t, updateServiceProvider]
  );

  if (!userProfile || !company) {
    return (
      <Stack alignItems="center">
        <CircularProgress
          color="primary"
          variant="indeterminate"
          sx={{ mt: 8 }}
        />
      </Stack>
    );
  }
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={8}>
        <ServiceProviderProfileInfo
          userProfile={userProfile}
          company={company}
          handleUpdateServiceProvider={handleUpdateServiceProvider}
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <Stack direction="column" gap={3}>
          <AccountStatusCard
            active={true}
            activeSince={userProfile.user.createdAt}
          />
          {company.stripeOperatedBy !==
            ServiceProviderStripeOperatedBy.Pflegenavi && (
            <ServiceProviderStripe />
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};
